import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { fetchBrandDrop, fetchNewProducts, hideNewProduct, rebuildNewProducts, showNewProduct, updateNewProductsDrop } from '../../api/new_products';
import Loader from '../General/Loader';
import NewProductTile from './NewProductTile';
import './NewProducts.scss';

const PAGE_SIZE = 40;
const SUGGESTION_LIMIT = 8;
const SEARCH_DEBOUNCE_MS = 250;

const NewProducts = () => {
  const [feed, setFeed] = useState([]);
  const [dropsLoaded, setDropsLoaded] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [selectedDrop, setSelectedDrop] = useState(null);
  const [isLoadingBrand, setIsLoadingBrand] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);

  const loadPage = async ({ reset = false, keepGrid = false } = {}) => {
    const offset = reset ? 0 : dropsLoaded;
    reset ? !keepGrid && setIsLoading(true) : setIsLoadingMore(true);
    try {
      const resp = await fetchNewProducts({ offset, limit: PAGE_SIZE });
      const drops = resp.drops || [];
      setDropsLoaded(offset + drops.length);
      setHasMore(drops.length === PAGE_SIZE);
      setFeed(prev => (reset ? resp.feed || [] : [...prev, ...(resp.feed || [])]));
    } catch (err) {
      window.ALERT.error(err || 'Failed to load new products.');
    } finally {
      reset ? setIsLoading(false) : setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    loadPage({ reset: true });
  }, []);

  useEffect(() => {
    const search = query.trim();
    setSuggestions(null);
    if (!search || selectedDrop) return;
    let cancelled = false;
    const debounce = setTimeout(() => {
      fetchNewProducts({ search, limit: SUGGESTION_LIMIT })
        .then(resp => !cancelled && setSuggestions(resp.drops || []))
        .catch(() => !cancelled && setSuggestions([]));
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [query, selectedDrop]);

  const selectedBrandName = selectedDrop?.brand.name || null;
  // The main view mirrors the creator feed; the brand view is the full membership, hidden products included, so they can be restored.
  const products = selectedDrop ? selectedDrop.products || [] : feed;
  const showSuggestions = !selectedDrop && !!query.trim() && suggestions !== null;
  const showLoadMore = !selectedDrop && !isLoading && hasMore && products.length > 0;

  const onKeyDown = e => {
    if (e.key === 'Enter' && suggestions?.length) selectBrand(suggestions[0]);
    else if (e.key === 'Escape') setQuery('');
  };

  const loadBrandDrop = async (Brand_id, { keepGrid = false } = {}) => {
    !keepGrid && setIsLoadingBrand(true);
    try {
      setSelectedDrop(await fetchBrandDrop(Brand_id));
    } catch (err) {
      window.ALERT.error(err || 'Failed to load this brand.');
    } finally {
      setIsLoadingBrand(false);
    }
  };

  const selectBrand = drop => {
    setSelectedDrop(drop);
    setQuery('');
    loadBrandDrop(drop.Brand_id);
  };

  const clearBrand = () => {
    setSelectedDrop(null);
    setQuery('');
  };

  const patchProduct = (product, patch) => {
    const patchList = list =>
      list.map(row => (row.ProductDrop_id === product.ProductDrop_id && row.Variant_id === product.Variant_id ? { ...row, ...patch } : row));
    setFeed(prev => patchList(prev));
    setSelectedDrop(prev => (prev ? { ...prev, products: patchList(prev.products || []) } : prev));
  };

  const toggleProductHidden = async product => {
    const isHidden = !product.isHidden;
    patchProduct(product, { isHidden });
    try {
      isHidden ? await hideNewProduct(product.ProductDrop_id, product.Variant_id) : await showNewProduct(product.ProductDrop_id, product.Variant_id);
    } catch (err) {
      patchProduct(product, { isHidden: product.isHidden });
      window.ALERT.error(err || 'Could not update this product.');
    }
  };

  const toggleBrandHidden = async () => {
    const isHidden = !selectedDrop.isHidden;
    setSelectedDrop(prev => ({ ...prev, isHidden }));
    try {
      await updateNewProductsDrop(selectedDrop.id, { isHidden });
      window.ALERT.success(isHidden ? `${selectedBrandName} is hidden from the creator feed.` : `${selectedBrandName} is back in the creator feed.`);
    } catch (err) {
      setSelectedDrop(prev => ({ ...prev, isHidden: !isHidden }));
      window.ALERT.error(err || 'Could not update this brand.');
    }
  };

  const rebuild = async () => {
    setIsRebuilding(true);
    try {
      const result = await rebuildNewProducts(selectedDrop.Brand_id);
      await Promise.all([loadBrandDrop(selectedDrop.Brand_id, { keepGrid: true }), loadPage({ reset: true, keepGrid: true })]);
      const { minted, added, restored, removed } = result.counters;
      window.ALERT.success(
        minted || added || restored || removed
          ? `Rebuilt the ${selectedBrandName} drop.`
          : `Nothing changed for ${selectedBrandName} in the last 7 days.`
      );
    } catch (err) {
      window.ALERT.error(err || 'Could not rebuild new products.');
    } finally {
      setIsRebuilding(false);
    }
  };

  const emptyCopy = selectedBrandName
    ? `No new products from ${selectedBrandName} this week.`
    : 'Nothing in New Products yet. The nightly build populates this after a Shopify sync.';

  return (
    <div className='new-products-admin-outer'>
      <div className='new-products-admin-inner'>
        <h1 className='page-title'>New Products</h1>
        <div className='toolbar'>
          <div className='brand-search'>
            <FontAwesomeIcon icon={faSearch} className='search-icon' />
            {selectedBrandName ? (
              <div className='selected-brand'>
                <div className='selected-brand-name'>{selectedBrandName}</div>
                <button type='button' className='clear-btn' onClick={clearBrand} aria-label='Clear brand'>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            ) : (
              <>
                <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={onKeyDown} placeholder='Search brands' />
                {!!query && (
                  <button type='button' className='clear-btn' onClick={() => setQuery('')} aria-label='Clear search'>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </>
            )}
            {showSuggestions && (
              <div className='search-suggestions'>
                {suggestions.length ? (
                  suggestions.map(drop => (
                    <div key={drop.id} className='search-suggestion' onClick={() => selectBrand(drop)}>
                      {drop.brand.name}
                    </div>
                  ))
                ) : (
                  <div className='search-suggestion empty'>No brands</div>
                )}
              </div>
            )}
          </div>
          {!!selectedDrop && (
            <div className='toolbar-actions'>
              <button
                type='button'
                className={cn('toolbar-button', { secondary: !selectedDrop.isHidden })}
                onClick={toggleBrandHidden}
                disabled={isLoadingBrand}
              >
                <FontAwesomeIcon icon={selectedDrop.isHidden ? faEyeSlash : faEye} />
                {selectedDrop.isHidden ? 'Hidden from creators' : 'Visible to creators'}
              </button>
              <button type='button' className='toolbar-button' onClick={rebuild} disabled={isRebuilding || isLoadingBrand}>
                {isRebuilding ? 'Rebuilding…' : 'Rebuild from catalog'}
              </button>
            </div>
          )}
        </div>
        {isLoading || isLoadingBrand ? (
          <Loader />
        ) : !products.length ? (
          <div className='empty-state'>{emptyCopy}</div>
        ) : (
          <div className='feed-grid'>
            {products.map(product => (
              <NewProductTile
                key={`${product.ProductDrop_id}-${product.Variant_id}`}
                product={product}
                onToggleHidden={() => toggleProductHidden(product)}
              />
            ))}
          </div>
        )}
        {showLoadMore && (
          <div className='load-more'>
            <button type='button' onClick={() => loadPage()} disabled={isLoadingMore}>
              {isLoadingMore ? 'Loading…' : 'Load more brands'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewProducts;
